<template>
  <section>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h1 class="mb-0">
        Imtihon dasturlari va taqdimotlar
      </h1>
      <b-link
        v-if="showAll"
        :to="{ name: 'exam-program-presentations-front' }"
        class="font-weight-bold"
      >
        Barchasini ko‘rish
      </b-link>
    </div>

    <b-card class="mb-2">
      <b-row>
        <b-col md="4">
          <b-form-group
            label="Qabul yili"
            class="mb-md-0"
          >
            <v-select
              v-model="selectedPeriodId"
              :reduce="option => option.id"
              :options="periods"
              :get-option-label="periodLabel"
              :clearable="false"
              placeholder="Yilni tanlang"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Tashkilot"
            class="mb-0"
          >
            <v-select
              v-model="selectedOrganizationId"
              :reduce="option => option.id"
              :options="organizationOptions"
              :get-option-label="organizationLabel"
              placeholder="Barcha tashkilotlar"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay
      :show="loading"
      rounded="sm"
    >
      <b-alert
        v-if="!loading && !visibleGroups.length"
        show
        variant="light"
      >
        Tanlangan filtr bo‘yicha ma’lumot topilmadi.
      </b-alert>

      <b-card
        v-for="group in visibleGroups"
        :key="group.organizationId"
        class="mb-1 organization-card"
      >
        <h3 class="mb-2">
          {{ organizationLabel(group) }}
        </h3>

        <div
          v-for="(item, index) in group.items"
          :key="item.id"
          :class="{ 'border-top pt-2 mt-2': index > 0 }"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <h4>{{ item.title }}</h4>
            <small class="text-muted">{{ formatDate(item.created_at) }}</small>
          </div>
          <p class="mb-1 preserve-lines">
            {{ item.description }}
          </p>
          <div>
            <b-link
              v-for="file in item.files"
              :key="file.id"
              :href="file.file"
              target="_blank"
              download
              class="d-inline-flex align-items-center mr-2 mb-50"
            >
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              {{ file.original_name }}
            </b-link>
          </div>
        </div>
      </b-card>
    </b-overlay>
  </section>
</template>

<script>
import {
  BAlert, BCard, BCol, BFormGroup, BLink, BOverlay, BRow,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@/libs/axios'

export default {
  components: {
    BAlert,
    BCard,
    BCol,
    BFormGroup,
    BLink,
    BOverlay,
    BRow,
    vSelect,
  },
  props: {
    limit: {
      type: Number,
      default: 100,
    },
    showAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      items: [],
      periods: [],
      selectedPeriodId: null,
      selectedOrganizationId: null,
      loading: false,
    }
  },
  computed: {
    organizationGroups() {
      const groups = new Map()
      this.items.forEach(item => {
        if (!groups.has(item.organization)) {
          groups.set(item.organization, {
            organizationId: item.organization,
            name: item.organization_name,
            items: [],
          })
        }
        groups.get(item.organization).items.push(item)
      })
      return Array.from(groups.values()).sort((first, second) => (
        this.organizationLabel(first).localeCompare(this.organizationLabel(second))
      ))
    },
    organizationOptions() {
      return this.organizationGroups.map(group => ({
        id: group.organizationId,
        name: group.name,
      }))
    },
    filteredGroups() {
      if (!this.selectedOrganizationId) return this.organizationGroups
      return this.organizationGroups.filter(group => String(group.organizationId) === String(this.selectedOrganizationId))
    },
    visibleGroups() {
      return this.showAll ? this.filteredGroups.slice(0, this.limit) : this.filteredGroups
    },
  },
  watch: {
    selectedPeriodId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.selectedOrganizationId = null
        this.loadItems()
      }
    },
  },
  created() {
    this.loadPeriods()
  },
  methods: {
    getList(data) {
      const list = data.results || data.data || data
      return Array.isArray(list) ? list : []
    },
    loadPeriods() {
      this.loading = true
      axios.get('/periods/', { params: { limit: 1200 } }).then(response => {
        this.periods = this.getList(response.data).sort((first, second) => second.admission_year - first.admission_year)
        this.selectedPeriodId = this.periods.length ? this.periods[0].id : null
        if (!this.selectedPeriodId) this.items = []
      }).finally(() => {
        if (!this.selectedPeriodId) this.loading = false
      })
    },
    loadItems() {
      this.loading = true
      axios.get('/exam-program-presentations/', {
        params: {
          limit: 1200,
          ordering: '-created_at',
          period: this.selectedPeriodId,
        },
      }).then(response => {
        this.items = this.getList(response.data)
      }).finally(() => {
        this.loading = false
      })
    },
    periodLabel(option) {
      return option ? String(option.admission_year) : ''
    },
    organizationLabel(option) {
      if (!option || !option.name) return '-'
      return typeof option.name === 'string' ? option.name : this.$langName(option.name)
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString(this.$i18n.locale)
    },
  },
}
</script>

<style scoped>
.preserve-lines {
  white-space: pre-line;
}

.organization-card {
  border-left: 3px solid var(--primary);
}
</style>
